import { Outlet, useNavigate, useOutletContext } from 'react-router-dom'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, Delete, Download, Pencil } from '@factorialco/f0-react/icons/app'
import {
  ProjectForSelectFragment,
  ProjectManagementValueObjectsBudgetStrategyTypeEnum,
  SubprojectDataCollectionItemFragment,
  SubprojectsDataCollectionDocument,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import {
  getStatusColor,
  getStatusLabel,
} from 'modules/project_management/components/labelTags/StatusTag'
import { formatProjectHours } from 'modules/project_management/lib/formatHours'
import { formatPercentage } from 'modules/project_management/lib/formatPercentage'
import { ISubprojectsPanelData } from 'modules/project_management/pages/ProjectDetailsPage/tabs/SubprojectsPage/types'

interface SubprojectsDataCollectionProps {
  projectId: number
  canExportData: boolean
  canManageSubprojects: boolean
  canSeeCosts: boolean
  projectCurrency: string
  hasSubprojectsEntityFeature: boolean
  onClick: (newData: ISubprojectsPanelData) => void
  budgetStrategyDelegation: boolean
  budgetStrategyBudgetType: ProjectManagementValueObjectsBudgetStrategyTypeEnum | null
  billableProject?: boolean
}

const SubprojectsDataCollection = ({
  projectId,
  canExportData,
  canManageSubprojects,
  canSeeCosts,
  projectCurrency,
  hasSubprojectsEntityFeature,
  onClick,
  budgetStrategyDelegation,
  budgetStrategyBudgetType,
  billableProject,
}: SubprojectsDataCollectionProps) => {
  const {
    access: { locale },
  } = useCurrentSession()

  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const { hasExpenses, hasFlexibleTracking, project } = useOutletContext<{
    hasExpenses: boolean
    hasFlexibleTracking: boolean
    project: ProjectForSelectFragment
  }>()

  const nonApplicable = i18n.t('finance.cost_centers_list.headers.job_catalog.not_applicable')

  const itemOnClick = (item: SubprojectDataCollectionItemFragment) => () => {
    onClick({
      inputedMinutes: item.inputedMinutes ?? 0,
      laborCostCents:
        (item.laborCostCents ?? 0) + (item.fixedCostCents ?? 0) + (item.spendingCostsCents ?? 0),
      subprojectId: item.id ?? 0,
      subprojectName: item.name ?? '',
    })
  }

  const calculateCostConversion = (cost: number | null | undefined) => {
    return MoneyWithCurrency({
      optionalDecimals: true,
      amount: cost ?? 0,
      currency: projectCurrency,
      locale,
    })
  }

  const calculateCostConversionOrFallback = (cost: number | null | undefined, fallback = '-') => {
    if (cost == null) return fallback

    return calculateCostConversion(cost)
  }

  const calculateTotalCost = (item: SubprojectDataCollectionItemFragment) =>
    (item.laborCostCents ?? 0) + (item.fixedCostCents ?? 0) + (item.spendingCostsCents ?? 0)

  const hasBudget = budgetStrategyBudgetType === 'total_budget'
  const fixedPrice = budgetStrategyBudgetType === 'project_fixed_cost'
  const noBudget = budgetStrategyBudgetType === 'without_budget' || budgetStrategyBudgetType == null
  const showEstimationColumns = hasSubprojectsEntityFeature && budgetStrategyDelegation && hasBudget
  const showProfitabilityColumns = hasSubprojectsEntityFeature && budgetStrategyDelegation

  const hoursColumn = () => {
    if (showEstimationColumns) {
      return {
        id: 'hours',
        label: i18n.t('project_management.table.subprojects_list.headers.hours_margin'),
        render: (item: SubprojectDataCollectionItemFragment) => {
          if (item.id == null) {
            return {
              type: 'text' as const,
              value: `${formatProjectHours(item.inputedMinutes)}`,
            }
          }

          return {
            type: 'text' as const,
            value: `${formatProjectHours(item.inputedMinutes)} / ${formatProjectHours(
              item.budgetStrategy?.plannedMinutes ?? 0
            )}`,
          }
        },
      }
    }

    return {
      id: 'hours',
      label: i18n.t('project_management.table.projects_list.headers.hours'),
      render: (item: SubprojectDataCollectionItemFragment) => {
        return {
          type: 'text' as const,
          value: formatProjectHours(item.inputedMinutes),
        }
      },
    }
  }

  const budgetColumn = () => {
    // Used budget (total_budget, no delegation, billable)
    if (hasSubprojectsEntityFeature && hasBudget && !budgetStrategyDelegation && billableProject) {
      return {
        id: 'budget',
        label: i18n.t(
          'project_management.overview.sections.financial_details.insights.revenue_to_date.tooltip_message.used_budget'
        ),
        align: 'right' as const,
        render: (item: SubprojectDataCollectionItemFragment) => {
          const hasValue = item.usedBudget != null
          return {
            type: 'text' as const,
            value: hasValue
              ? `${calculateCostConversion(item.usedBudget)}`
              : { placeholder: nonApplicable },
          }
        },
      }
    }

    // Planned income (project_fixed_cost, with delegation)
    if (hasSubprojectsEntityFeature && fixedPrice && budgetStrategyDelegation) {
      return {
        id: 'budget',
        label: i18n.t('project_management.table.subprojects_list.headers.planned_income'),
        align: 'right' as const,
        render: (item: SubprojectDataCollectionItemFragment) => {
          const hasValue = item.budgetStrategy?.feeAmountCents != null && item.id != null
          return {
            type: 'text' as const,
            value: hasValue
              ? `${calculateCostConversion(item.budgetStrategy?.feeAmountCents ?? 0)}`
              : { placeholder: nonApplicable },
          }
        },
      }
    }

    // Used / budget cap (total_budget, with delegation)
    if (showEstimationColumns) {
      return {
        id: 'budget',
        label: i18n.t('project_management.table.subprojects_list.headers.budget_margin'),
        align: 'right' as const,
        render: (item: SubprojectDataCollectionItemFragment) => {
          const hasValue = item.budgetStrategy?.feeAmountCents != null
          return {
            type: 'text' as const,
            value: hasValue
              ? `${calculateCostConversion(item.usedBudget)} / ${calculateCostConversion(
                  item.budgetStrategy?.feeAmountCents ?? 0
                )}`
              : { placeholder: nonApplicable },
          }
        },
      }
    }

    // Billable to date (without_budget)
    if (noBudget && billableProject) {
      return {
        id: 'budget',
        label: i18n.t(
          'project_management.overview.sections.financial_details.insights.revenue_to_date.tooltip_message.billable_to_date'
        ),
        align: 'right' as const,
        render: (item: SubprojectDataCollectionItemFragment) => {
          const hasValue = item.usedBudget != null && item.id != null
          return {
            type: 'text' as const,
            value: hasValue
              ? `${calculateCostConversion(item.usedBudget)}`
              : { placeholder: nonApplicable },
          }
        },
      }
    }

    return null
  }

  const costColumn = () => {
    if (!canSeeCosts) return null

    // Cost margin
    if (hasSubprojectsEntityFeature && budgetStrategyDelegation && !noBudget) {
      return {
        id: 'cost',
        label: i18n.t('project_management.table.subprojects_list.headers.cost_margin'),
        align: 'right' as const,
        render: (item: SubprojectDataCollectionItemFragment) => {
          if (item.id == null) {
            return {
              type: 'text' as const,
              value: `${calculateCostConversion(calculateTotalCost(item))}`,
            }
          }

          return {
            type: 'text' as const,
            value: `${calculateCostConversion(
              calculateTotalCost(item)
            )} / ${calculateCostConversion(item.budgetStrategy?.plannedCents ?? 0)}`,
          }
        },
      }
    }

    // Just cost
    return {
      id: 'cost',
      label: hasSubprojectsEntityFeature
        ? i18n.t('project_management.table.subprojects_list.headers.cost')
        : i18n.t('project_management.table.subprojects_list.headers.labor_cost'),
      align: 'right' as const,
      render: (item: SubprojectDataCollectionItemFragment) => {
        if (hasSubprojectsEntityFeature) {
          const totalCost = calculateTotalCost(item)
          return {
            type: 'text' as const,
            value: calculateCostConversion(totalCost),
          }
        }
        return {
          type: 'text' as const,
          value: calculateCostConversion(item.laborCostCents ?? 0),
        }
      },
    }
  }

  const profitColumn = () => {
    if (!hasSubprojectsEntityFeature || !budgetStrategyDelegation || !canSeeCosts) return null

    // Profit margin
    if (!noBudget) {
      return {
        id: 'profit',
        label: i18n.t('project_management.table.subprojects_list.headers.profit_margin'),
        align: 'right' as const,
        render: (item: SubprojectDataCollectionItemFragment) => {
          if (item.id == null) {
            return {
              type: 'text' as const,
              value: calculateCostConversionOrFallback(item.currentProfitCents),
            }
          }

          return {
            type: 'text' as const,
            value: `${calculateCostConversionOrFallback(
              item.currentProfitCents
            )} / ${calculateCostConversionOrFallback(item.estimatedProfitCents)}`,
          }
        },
      }
    }

    // Just profit
    return {
      id: 'profit',
      label: i18n.t('project_management.table.subprojects_list.headers.profit'),
      align: 'right' as const,
      render: (item: SubprojectDataCollectionItemFragment) => {
        return {
          type: 'text' as const,
          value: calculateCostConversionOrFallback(item.currentProfitCents),
        }
      },
    }
  }

  const profitabilityPercentageColumn = () => {
    if (!canSeeCosts || !showProfitabilityColumns) return null

    const isNoBudgetPhase = noBudget && budgetStrategyDelegation

    const label = isNoBudgetPhase
      ? i18n.t('project_management.table.subprojects_list.headers.current_margin')
      : i18n.t('project_management.table.subprojects_list.headers.profitability_percentage')

    return {
      id: 'profitability_percentage',
      label,
      align: 'right' as const,
      render: (item: SubprojectDataCollectionItemFragment) => {
        if (item.id == null) {
          return {
            type: 'text' as const,
            value: { placeholder: nonApplicable },
          }
        }

        return {
          type: 'text' as const,
          value: isNoBudgetPhase
            ? formatPercentage(item.currentMarginPercentage)
            : `${formatPercentage(item.currentMarginPercentage)} / ${formatPercentage(
                item.estimatedMarginPercentage
              )}`,
        }
      },
    }
  }

  const alertsColumn = () => {
    if (!canSeeCosts || !showProfitabilityColumns || noBudget) return null

    return {
      id: 'alerts',
      label: i18n.t('project_management.table.subprojects_list.headers.alerts'),
      render: (item: SubprojectDataCollectionItemFragment) => {
        if (item.id == null) {
          return {
            type: 'text' as const,
            value: { placeholder: nonApplicable },
          }
        }

        switch (item.alertType) {
          case 'overbudget':
            return {
              type: 'alertTag' as const,
              value: {
                label: i18n.t('project_management.table.subprojects_list.alerts.overbudget'),
                level: 'warning' as const,
              },
            }
          case 'unprofitable':
            return {
              type: 'alertTag' as const,
              value: {
                label: i18n.t('project_management.table.subprojects_list.alerts.unprofitable'),
                level: 'critical' as const,
              },
            }
          case 'close_to_cap':
            return {
              type: 'alertTag' as const,
              value: {
                label: i18n.t('project_management.table.subprojects_list.alerts.close_to_cap'),
                level: 'info' as const,
              },
            }
          default:
            return {
              type: 'text' as const,
              value: '-',
            }
        }
      },
    }
  }

  const startDateColumn = () => {
    if (!hasSubprojectsEntityFeature) return null
    return {
      id: 'start_date',
      label: i18n.t('project_management.table.subprojects_list.headers.start_date'),
      render: (item: SubprojectDataCollectionItemFragment) => ({
        type: 'date' as const,
        value:
          item.id == null
            ? { placeholder: nonApplicable }
            : item.startDate
              ? new Date(item.startDate)
              : undefined,
      }),
      sorting: 'startDate' as const,
    }
  }

  const dueDateColumn = () => {
    if (!hasSubprojectsEntityFeature) return null
    return {
      id: 'due_date',
      label: i18n.t('project_management.table.subprojects_list.headers.end_date'),
      render: (item: SubprojectDataCollectionItemFragment) => ({
        type: 'date' as const,
        value:
          item.id == null
            ? { placeholder: nonApplicable }
            : item.dueDate
              ? new Date(item.dueDate)
              : undefined,
      }),
      sorting: 'dueDate' as const,
    }
  }

  const source = useGraphqlDataCollectionSource({
    document: SubprojectsDataCollectionDocument,
    mapData: (data) => {
      return data.projectManagement.subprojectsConnection.nodes
    },
    mapVariables: ({ search, sortings }) => ({
      projectIds: [projectId],
      includeCost: canSeeCosts,
      includeProfitability: canSeeCosts && showProfitabilityColumns,
      hasSubprojectsEntity: hasSubprojectsEntityFeature,
      name: search,
      sortOrder: sortings?.[0],
    }),
    mapPagination: (data) => ({
      total: data.projectManagement.subprojectsConnection.totalCount ?? 0,
    }),
    totalItemSummary: (totals) => {
      return i18n.tp('project_management.subprojects.total_items', { count: totals })
    },
    itemOnClick: itemOnClick,
    itemActions: canManageSubprojects
      ? (item) => {
          if (item.id !== null) {
            return compact([
              !hasSubprojectsEntityFeature
                ? {
                    label: i18n.t('project_management.table.subprojects_list.menu_links.edit_name'),
                    icon: Pencil,
                    onClick: () => {
                      track(trackingEvents.projectManagement.subprojects.editSubprojectStart, {
                        project_id: projectId,
                      })

                      navigate(
                        navigation.projects.id.details.subprojects.subprojectId.edit.fullPath({
                          id: projectId,
                          subprojectId: Number(item.id),
                        }),
                        { state: { subprojectName: item.name } }
                      )
                    },
                  }
                : undefined,
              {
                label: i18n.t(
                  'project_management.table.subprojects_list.menu_links.delete_subproject'
                ),
                icon: Delete,
                critical: true,
                onClick: () => {
                  track(trackingEvents.projectManagement.subprojects.deleteSubprojectStart, {
                    project_id: projectId,
                  })

                  navigate(
                    navigation.projects.id.details.subprojects.subprojectId.delete.fullPath({
                      id: projectId,
                      subprojectId: Number(item.id),
                    }),
                    { state: { subprojectName: item.name } }
                  )
                },
              },
            ])
          }

          return undefined
        }
      : undefined,
    primaryActions: () =>
      canManageSubprojects
        ? {
            label: i18n.t('project_management.subprojects.add_button'),
            icon: Add,
            onClick: () => {
              track(trackingEvents.projectManagement.subprojects.addSubprojectStart, {
                project_id: Number(projectId),
              })
              navigate(
                navigation.projects.id.details.subprojects.create.fullPath({
                  id: Number(projectId),
                })
              )
            },
          }
        : undefined,
    secondaryActions: () =>
      canExportData
        ? [
            {
              label: i18n.t('project_management.table.projects_list.table_options.export.title'),
              description: i18n.t(
                'project_management.table.projects_list.table_options.export.description'
              ),
              icon: Download,
              onClick: () => {
                track(trackingEvents.projectManagement.projects.downloadProjectsReportStart, {
                  origin: 'Phases',
                })
                navigate(
                  navigation.projects.id.details.subprojects.export.fullPath({ id: projectId }),
                  { state: { projectForSelect: project } }
                )
              },
            },
          ]
        : undefined,
    search: {
      enabled: true,
    },
    sortings: {
      name: {
        label: i18n.t('project_management.table.subprojects_list.headers.subproject'),
      },
      ...(hasSubprojectsEntityFeature && {
        status: {
          label: i18n.t('project_management.table.projects_list.headers.status'),
        },
        startDate: {
          label: i18n.t('project_management.table.subprojects_list.headers.start_date'),
        },
        dueDate: {
          label: i18n.t('project_management.table.subprojects_list.headers.end_date'),
        },
      }),
    },
    defaultSortings: {
      field: 'name',
      order: 'asc',
    },
  })

  return (
    <>
      <OneDataCollection
        id="project_management/subprojects/v1"
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              allowColumnHiding: true,
              allowColumnReordering: true,
              columns: compact([
                {
                  id: 'subproject',
                  label: i18n.t('project_management.table.subprojects_list.headers.subproject'),
                  render: (item: SubprojectDataCollectionItemFragment) => ({
                    type: 'text',
                    value:
                      item.id != null
                        ? item.name
                        : {
                            placeholder: i18n.t(
                              'project_management.subprojects.without_subproject_alerts.not_delegated.title'
                            ),
                          },
                  }),
                  sorting: 'name' as const,
                  noHiding: true,
                },
                hasSubprojectsEntityFeature && {
                  id: 'status',
                  label: i18n.t('project_management.table.projects_list.headers.status'),
                  render: (item: SubprojectDataCollectionItemFragment) => {
                    if (item.id == null || !item.status) {
                      return {
                        type: 'text' as const,
                        value: { placeholder: nonApplicable },
                      }
                    }

                    return {
                      type: 'status' as const,
                      value: {
                        label: getStatusLabel(item.status),
                        status: getStatusColor(item.status),
                      },
                    }
                  },
                  sorting: 'status' as const,
                },
                hoursColumn(),
                budgetColumn(),
                costColumn(),
                profitColumn(),
                profitabilityPercentageColumn(),
                alertsColumn(),
                startDateColumn(),
                dueDateColumn(),
              ]),
            },
          },
        ]}
      />
      <Outlet context={{ hasExpenses, hasFlexibleTracking }} />
    </>
  )
}

export default SubprojectsDataCollection
